import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getGuideDetailService,
  getGuidesCatalogService,
  getMyPurchasedGuidesService,
} from "../services/Guides2Service";
import { GUIDE_VARIANTS } from "../../shared/constants/guideVariants";
import { waitForPaymentAccess } from "./useGuidePurchase";

export const GUIDES2_FILTERS = { ALL: "all", PURCHASED: "purchased" };

export const GUIDES2_PAGE_SIZE = 12;

// Cuanto sigue esperando el catalogo a que el webhook habilite una guia recien
// pagada, DESPUES de que el panel de compra se diera por vencido (20 s, ver
// ACCESS_POLL_* en useGuidePurchase). 24 vueltas de 5 s = 2 minutos mas, sin
// apurar al servidor: a estas alturas el pago ya esta hecho y lo unico que falta
// es que se vea.
export const ACTIVATION_POLL_ATTEMPTS = 24;
export const ACTIVATION_POLL_INTERVAL_MS = 5000;

// Lee una propiedad venga en camelCase o en PascalCase: el backend serializa en
// camelCase, pero sus DTO están declarados en PascalCase y aceptar las dos
// formas evita que un cambio de configuración del serializador deje la pantalla
// vacía sin ningún error visible.
const pick = (source, name) => {
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return source?.[name] ?? source?.[pascal];
};

// Primer valor con texto de verdad. El catálogo devuelve `name` vacío y
// `description` en null cuando la guía no está traducida al idioma activo
// (comprobado en PRE: en EN las mismas guías que en ES traen texto llegan
// vacías), y el detalle responde igual.
//
// Antes de rendirse se mira el OTRO idioma: si la guía solo está escrita en
// español, en la interfaz en inglés se muestra el español —leer el texto en
// el idioma equivocado sirve; una tarjeta muda, no—. El último recurso sigue
// siendo el `code` publicado, que siempre viene.
const firstText = (...values) => {
  const found = values.find((value) => String(value ?? "").trim() !== "");
  return found ? String(found).trim() : "";
};

const hasText = (value) => String(value ?? "").trim() !== "";

// Una compra sin fecha legible se va al final del desempate, para que nunca le
// gane a una que sí la trae.
const purchaseTime = (purchase) => {
  const parsed = Date.parse(pick(purchase, "purchaseDate") ?? "");
  return Number.isNaN(parsed) ? Infinity : parsed;
};

// mis-guias-compradas puede devolver la MISMA compra repetida: comprobado en
// pre el 25/09/2026 tras comprar la guía 55, que volvió en dos filas idénticas
// —mismo guideGuid, misma purchaseDate al segundo, mismo monto—. La pestaña de
// compradas pintaba entonces dos tarjetas de la misma guía.
//
// La tarjeta es POR GUÍA, no por registro de compra, así que se colapsa por
// guideId. De las repetidas se conserva la MÁS ANTIGUA, que es desde cuándo se
// tiene acceso; quedarse con "la primera que llegue" haría que la fecha
// mostrada bailara según el orden en que respondiera el servidor.
//
// El catálogo no se veía afectado porque cruza las compras con un Map por
// guideId, que ya colapsa las repetidas; el fallo solo salía en la pestaña de
// compradas, que recorre la lista tal cual.
//
// Una fila sin guideId no se puede agrupar, así que se conserva tal cual en vez
// de fundirla con las demás.
export const dedupePurchases = (rows) => {
  const byGuideId = new Map();
  const sinGuia = [];

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const guideId = pick(row, "guideId");
    if (guideId === undefined || guideId === null) {
      sinGuia.push(row);
      return;
    }

    const previa = byGuideId.get(guideId);
    // El Map conserva la posición de la primera aparición aunque se sustituya
    // el valor, así que el orden en que respondió el servidor no se altera.
    if (!previa || purchaseTime(row) < purchaseTime(previa)) byGuideId.set(guideId, row);
  });

  return [...byGuideId.values(), ...sinGuia];
};

// Que le falta a esta fila del catalogo, y por tanto justifica pedir su detalle:
//  - el acceso por variante de pago, que solo dice obtener/detalle-guia;
//  - el nombre o la descripcion, cuando el catalogo los devuelve vacios porque
//    la guia no esta traducida al idioma activo.
// Una guia solo gratuita y con sus textos completos no gana nada con el detalle,
// asi que no se pide: es una llamada menos por tarjeta.
const needsDetail = (item) =>
  pick(item, "hasPaidVariant") === true ||
  !hasText(pick(item, "name")) ||
  !hasText(pick(item, "description"));

// Desde VIVA-312 el catálogo devuelve una fila por GUÍA con hasFreeVariant /
// hasPaidVariant, en vez de una fila por variante. La tarjeta es una sola
// aunque la guía tenga las dos modalidades.
//
// `detailRows` es la respuesta de obtener/detalle-guia para esa guía: una fila
// por variante activa. Cuando llegó, manda sobre el catálogo en dos cosas:
//  - el acceso, porque `hasAccess` lo resuelve el backend por variante; sin
//    detalle se cae al cruce con las compras, que es lo que había antes;
//  - el texto, porque puede traer nombre/descripción donde el catálogo no.
// obtener/contenido-guia vuelve a revisar el acceso al abrir la guía; esto
// solo evita ofrecer un botón que va a terminar en "no tienes acceso".
const buildCard = (item, purchase, detailRows, fallbackText) => {
  const rows = Array.isArray(detailRows) ? detailRows : [];
  const freeRow = rows.find(
    (row) => pick(row, "guideVariantTypeCode") === GUIDE_VARIANTS.FREE
  );
  const paidRow = rows.find(
    (row) => pick(row, "guideVariantTypeCode") === GUIDE_VARIANTS.PAID
  );

  const code = pick(item, "code") ?? "";
  const hasFreeVariant = pick(item, "hasFreeVariant") === true || Boolean(freeRow);
  const hasPaidVariant = pick(item, "hasPaidVariant") === true || Boolean(paidRow);

  return {
    guideId: pick(item, "guideId"),
    guideGuid: pick(item, "guideGuid"),
    code,
    name: firstText(
      pick(item, "name"),
      freeRow && pick(freeRow, "name"),
      paidRow && pick(paidRow, "name"),
      fallbackText && fallbackText.name,
      code
    ),
    description: firstText(
      pick(item, "description"),
      freeRow && pick(freeRow, "description"),
      paidRow && pick(paidRow, "description"),
      fallbackText && fallbackText.description
    ),
    coverImageUrl: firstText(
      pick(item, "coverImageUrl"),
      freeRow && pick(freeRow, "coverImageUrl"),
      paidRow && pick(paidRow, "coverImageUrl")
    ),
    // `price` es el nombre nuevo; se acepta `cost` por si alguna respuesta
    // todavía viene con el anterior — el detalle sigue llamándolo así.
    price:
      pick(item, "price") ??
      pick(item, "cost") ??
      (paidRow ? pick(paidRow, "cost") : undefined),
    currencyCode: firstText(
      pick(item, "currencyCode"),
      paidRow && pick(paidRow, "currencyCode"),
      freeRow && pick(freeRow, "currencyCode")
    ),
    categoryName: firstText(
      pick(item, "categoryName"),
      freeRow && pick(freeRow, "categoryName"),
      paidRow && pick(paidRow, "categoryName"),
      fallbackText && fallbackText.categoryName
    ),
    authorNickName: firstText(
      pick(item, "authorNickName"),
      freeRow && pick(freeRow, "authorNickName"),
      paidRow && pick(paidRow, "authorNickName")
    ),
    // VIVA-1012: el catalogo manda `isBeta` en cada fila y con eso se pinta el
    // distintivo de la tarjeta.
    //
    // `obtener/detalle-guia` tambien lo devuelve (comprobado el 22/09/2026),
    // pero NO se usa como respaldo como en los demas campos: el catalogo nunca
    // lo omite —a diferencia del nombre y la descripcion, que llegan vacios
    // cuando la guia no esta traducida—, asi que el respaldo no tendria a quien
    // ayudar y solo sumaria una fuente mas de la que dudar.
    isBeta: pick(item, "isBeta") === true,
    hasFreeVariant,
    hasPaidVariant,
    purchaseDate: purchase ? pick(purchase, "purchaseDate") : null,
    canReadFree: freeRow ? pick(freeRow, "hasAccess") === true : hasFreeVariant,
    // Una compra registrada nunca pierde el botón de leer, aunque el detalle
    // diga que no hay acceso: si las dos fuentes se contradicen, quitarle la
    // guía a quien la pagó es el error caro. obtener/contenido-guia vuelve a
    // revisarlo al abrirla y es quien manda de verdad.
    canReadPaid: paidRow
      ? pick(paidRow, "hasAccess") === true || Boolean(purchase)
      : hasPaidVariant && Boolean(purchase),
  };
};

// Catálogo de guías más las compras del usuario.
//
// El catálogo ya no es anónimo: corre bajo BearerPublic y exige sesión, así que
// sin usuario ni siquiera se intenta la llamada — se reporta como tal para que
// la pantalla invite a iniciar sesión en vez de mostrar un error genérico.
//
// El detalle SÍ se pide por tarjeta (una llamada por guía de la página
// visible, en paralelo). Se había quitado por ser N+1, y se reconectó el
// 17/09 por dos razones concretas: el catálogo devuelve el nombre y la
// descripción vacíos cuando la guía no está traducida al idioma activo, y
// `hasAccess` por variante solo lo dice el detalle —antes se deducía cruzando
// con las compras—. Nunca bloquea al listado: las tarjetas se pintan con el
// catálogo y se completan cuando llega su detalle.
// `onlyPaid` es la vista de venta (/guides2/contratar): deja fuera las guias
// que no tienen nada que cobrar.
//
// El filtro vive AQUI y no en la pantalla porque la paginacion la hace el
// servidor, de 12 en 12 sobre el catalogo completo. Filtrando despues, en la
// pantalla, una pagina cuyas 12 guias fueran gratuitas se quedaba sin tarjetas
// y ademas sin paginacion —la barra se escondia al no haber nada que mostrar—,
// dejando al visitante atrapado sin forma de llegar a la pagina siguiente.
// Teniendolo aqui, `hasNextPage` sigue hablando del catalogo del servidor, que
// es lo unico que sabe si hay mas.
// `initialFilter` es con que pestana arranca el listado. La pestana "Mis guias"
// del perfil abre directamente en las compradas: sin esto pintaba el catalogo
// completo en el primer render y se corregia despues, que es justo el parpadeo
// que hace dudar de si la pantalla mostro lo que debia.
export const useGuides2Catalog = ({
  lang,
  userId,
  onlyPaid = false,
  initialFilter = GUIDES2_FILTERS.ALL,
}) => {
  const [filter, setFilter] = useState(initialFilter);
  const [page, setPage] = useState(1);
  const [purchasedPage, setPurchasedPage] = useState(1);
  const [rawItems, setRawItems] = useState([]);
  const [fallbackTextByGuideId, setFallbackTextByGuideId] = useState({});
  const [detailsByGuideId, setDetailsByGuideId] = useState({});
  const requestedDetailsRef = useRef(new Set());
  // Cuantas peticiones de detalle siguen en el aire. El catalogo responde antes
  // que ellas, asi que entre una cosa y la otra la tarjeta se pinta con el
  // `code` en vez del nombre y sin su boton; esto es lo que permite decir en
  // pantalla que todavia se estan cargando datos en vez de aparentar que ya
  // esta. Se descuenta siempre —incluso si la pagina cambio a media peticion—
  // para que el aviso no se quede encendido.
  const [pendingDetails, setPendingDetails] = useState(0);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // `purchasesRequestRef` descarta respuestas viejas: si el idioma cambia o se
  // recarga a mitad de una peticion, solo la ultima pinta.
  const purchasesRequestRef = useRef(0);

  const loadPurchases = useCallback(async () => {
    const requestId = ++purchasesRequestRef.current;

    if (!userId) {
      setPurchases([]);
      return;
    }

    try {
      const result = await getMyPurchasedGuidesService({ lang });
      if (requestId === purchasesRequestRef.current) {
        setPurchases(dedupePurchases(result?.data));
      }
    } catch {
      if (requestId === purchasesRequestRef.current) setPurchases([]);
    }
  }, [lang, userId]);

  useEffect(() => {
    loadPurchases();

    return () => {
      // Invalida la peticion en curso al desmontar o cambiar de idioma.
      purchasesRequestRef.current += 1;
    };
  }, [loadPurchases]);

  const loadCatalog = useCallback(async () => {
    if (!userId) {
      setRawItems([]);
      setError("auth");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await getGuidesCatalogService({
        lang,
        page,
        pageSize: GUIDES2_PAGE_SIZE,
      });
      setRawItems(Array.isArray(result?.data) ? result.data : []);
    } catch (err) {
      // Sin console.error: el repository ya dejo esta misma falla en consola.
      setRawItems([]);
      setError(err?.response?.status === 401 ? "auth" : "catalog");
    } finally {
      setLoading(false);
    }
  }, [lang, page, userId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // Catálogo en el otro idioma, para las guías escritas en uno solo.
  //
  // Es UNA llamada por página, no una por tarjeta: la lista de guías es la
  // misma en los dos idiomas y solo cambian los textos, así que se cruza por
  // guideId. Solo se usa como respaldo —nunca pisa un texto que el idioma
  // activo sí trajo—, y si falla, las tarjetas siguen su camino hasta el
  // `code`, que es lo que hacían antes de esto.
  //
  // Y se pide SOLO cuando hay algo que rellenar: si todas las guías de la página
  // traen su nombre y su descripción, esta llamada no tenía a quién ayudar. Por
  // eso ahora depende de `rawItems` y no de la página: necesita ver la respuesta
  // del catálogo para saber si hace falta.
  useEffect(() => {
    let active = true;

    if (!userId) {
      setFallbackTextByGuideId({});
      return undefined;
    }

    const faltaTexto = rawItems.some(
      (item) => !hasText(pick(item, "name")) || !hasText(pick(item, "description"))
    );

    if (!faltaTexto) {
      setFallbackTextByGuideId({});
      return undefined;
    }

    const loadFallbackTexts = async () => {
      try {
        const result = await getGuidesCatalogService({
          lang: lang === "en" ? "es" : "en",
          page,
          pageSize: GUIDES2_PAGE_SIZE,
        });

        if (!active) return;

        const next = {};
        (Array.isArray(result?.data) ? result.data : []).forEach((item) => {
          next[pick(item, "guideId")] = {
            name: pick(item, "name"),
            description: pick(item, "description"),
            categoryName: pick(item, "categoryName"),
          };
        });

        setFallbackTextByGuideId(next);
      } catch {
        // Sin console.error: el repository ya dejo esta misma falla en consola.
        // Aqui solo importa que las tarjetas sigan su camino hasta el `code`.
        if (active) setFallbackTextByGuideId({});
      }
    };

    loadFallbackTexts();

    return () => {
      active = false;
    };
  }, [lang, rawItems, userId]);

  // El detalle trae textos, asi que al cambiar de idioma lo guardado ya no sirve
  // y se vuelve a preguntar. Va declarado ANTES del efecto que pide, para que en
  // ese mismo ciclo el recuerdo ya este limpio.
  useEffect(() => {
    requestedDetailsRef.current = new Set();
    setDetailsByGuideId({});
  }, [lang]);

  // Detalle de las guías de la página visible. Se pide después del catálogo y
  // en paralelo, y su resultado solo completa lo ya pintado: si una guía falla
  // —obtener/detalle-guia responde 404 cuando no tiene variantes activas— esa
  // tarjeta se queda con lo que traía el catálogo, y las demás no se ven
  // afectadas. Por eso allSettled y no Promise.all.
  //
  // Dos recortes sobre el N+1 con el que nacio:
  //  - solo se pregunta por las guías a las que el detalle les aporta algo
  //    (ver `needsDetail`): una guía solo gratuita y con sus textos completos
  //    no gana nada;
  //  - y solo UNA vez por guía. `requestedDetailsRef` recuerda lo ya pedido, de
  //    modo que pasar a la página 2 y volver a la 1 ya no vuelve a preguntar por
  //    las mismas doce guías.
  //
  // Una guía cuyo detalle falló queda marcada como pedida y no se reintenta: el
  // 404 de las guías sin variantes activas es esperado, y reintentarlo en cada
  // vuelta costaría mas que no tenerlo.
  useEffect(() => {
    let active = true;

    const pendientes = rawItems
      .filter(needsDetail)
      .map((item) => pick(item, "guideId"))
      .filter(
        (guideId) =>
          guideId !== undefined &&
          guideId !== null &&
          !requestedDetailsRef.current.has(guideId)
      );

    if (pendientes.length === 0) return undefined;

    pendientes.forEach((guideId) => requestedDetailsRef.current.add(guideId));
    setPendingDetails((previous) => previous + pendientes.length);

    const loadDetails = async () => {
      try {
        const results = await Promise.allSettled(
          pendientes.map((guideId) => getGuideDetailService({ guideId, lang }))
        );

        if (!active) return;

        const nextDetails = {};
        results.forEach((result, index) => {
          if (result.status === "fulfilled" && Array.isArray(result.value?.data)) {
            nextDetails[pendientes[index]] = result.value.data;
          }
        });

        // Se acumula sobre lo que ya habia: lo de las paginas ya vistas se
        // conserva, que es lo que permite no volver a pedirlo.
        setDetailsByGuideId((previous) => ({ ...previous, ...nextDetails }));
      } catch (err) {
        // Este SI registra, y no duplica nada: `Promise.allSettled` nunca
        // rechaza, asi que ningun fallo de red llega hasta aqui —esos ya los
        // dejo el repository—. Lo unico que puede caer en este catch es un
        // error de programacion del propio bloque, que si conviene ver.
        console.error("Fallo inesperado al armar el detalle de las guías:", err);
      } finally {
        setPendingDetails((previous) => Math.max(0, previous - pendientes.length));
      }
    };

    loadDetails();

    return () => {
      active = false;
    };
  }, [rawItems, lang]);

  // VIVA-1214 — tras pagar, volver a preguntar por el acceso de ESA guía.
  //
  // No basta con recargar las compras: el acceso de la variante de pago lo
  // resuelve `obtener/detalle-guia`, y su respuesta se guarda UNA sola vez por
  // guía (`requestedDetailsRef`). El detalle pedido antes de pagar decía
  // `hasAccess: false` y nadie volvía a preguntar, así que la tarjeta dependía
  // únicamente de que la compra apareciera en mis-guias-compradas. Aquí se
  // vuelven a pedir las dos, que son las dos fuentes de `buildCard`.
  const refreshGuideAccess = useCallback(
    async (guideId) => {
      const refrescarDetalle = async () => {
        if (guideId === undefined || guideId === null || guideId === "") return;
        try {
          const result = await getGuideDetailService({ guideId, lang });
          if (!Array.isArray(result?.data)) return;
          setDetailsByGuideId((previous) => ({ ...previous, [guideId]: result.data }));
        } catch {
          // Sin console.error: el repository ya dejo la falla en consola. La
          // tarjeta se queda con lo que ya tenia y con las compras recargadas.
        }
      };

      await Promise.allSettled([loadPurchases(), refrescarDetalle()]);
    },
    [lang, loadPurchases]
  );

  // VIVA-1214 — una guia pagada cuyo webhook todavia no ha corrido.
  //
  // El panel de compra espera 20 s y se rinde; hasta ahora ahi terminaba todo y
  // la tarjeta se quedaba en "Ver precio" aunque la guia se habilitara dos
  // segundos despues: nadie volvia a preguntar y solo recargar la pagina a mano
  // lo arreglaba. Esa espera no puede vivir en el panel —se desmonta en cuanto
  // la tarjeta deja de ofrecer la compra o el comprador cierra el precio—, asi
  // que vive aqui, que es lo que dura toda la visita.
  const [activation, setActivation] = useState(null);
  const [activatedGuideId, setActivatedGuideId] = useState(null);
  // La espera se agoto sin que la guia quedara habilitada. Quien lo consume
  // decide que hacer; hoy la pantalla recarga (ver el parche de VIVA-1499).
  const [activationExpired, setActivationExpired] = useState(false);

  const awaitGuideActivation = useCallback(({ guideId, paymentIntentId } = {}) => {
    setActivatedGuideId(null);
    setActivationExpired(false);
    setActivation({ guideId, paymentIntentId });
  }, []);

  useEffect(() => {
    if (!activation) return undefined;

    let activo = true;

    const esperarAlWebhook = async () => {
      const habilitada = await waitForPaymentAccess({
        paymentIntentId: activation.paymentIntentId,
        guideId: activation.guideId,
        lang,
        attempts: ACTIVATION_POLL_ATTEMPTS,
        intervalMs: ACTIVATION_POLL_INTERVAL_MS,
        isActive: () => activo,
      });

      if (!activo) return;

      setActivation(null);
      if (!habilitada) {
        setActivationExpired(true);
        return;
      }

      setActivatedGuideId(activation.guideId);
      await refreshGuideAccess(activation.guideId);
    };

    esperarAlWebhook();

    return () => {
      activo = false;
    };
  }, [activation, lang, refreshGuideAccess]);

  // El cruce va por el id EN TEXTO. Las dos listas las sirve el backend, pero no
  // el mismo endpoint, y basta con que una mande el id como número y la otra
  // como cadena para que un `Map` —que compara sin convertir— no encuentre la
  // compra: la guía quedaba pagada y la tarjeta seguía ofreciendo "Ver precio",
  // sin ningún error que lo delatara. El resto de la feature ya compara así
  // (ver `guideIsPurchased` en useGuidePurchase).
  const purchaseByGuideId = useMemo(
    () => new Map(purchases.map((purchase) => [String(pick(purchase, "guideId")), purchase])),
    [purchases]
  );

  const cards = useMemo(
    () =>
      rawItems.map((item) => {
        const guideId = pick(item, "guideId");
        return buildCard(
          item,
          purchaseByGuideId.get(String(guideId)),
          detailsByGuideId[guideId],
          fallbackTextByGuideId[guideId]
        );
      }),
    [rawItems, purchaseByGuideId, detailsByGuideId, fallbackTextByGuideId]
  );

  // La pestaña de compradas se arma desde su propio endpoint, que es la fuente
  // real: una guía comprada puede estar en otra página del catálogo o haberse
  // despublicado, y aun así el usuario debe poder abrirla. Una compra siempre
  // es de la variante de pago.
  const purchasedCards = useMemo(() => {
    const cardByGuideId = new Map(cards.map((card) => [String(card.guideId), card]));

    return purchases.map((purchase) => {
      const guideId = pick(purchase, "guideId");
      const fromCatalog = cardByGuideId.get(String(guideId));
      if (fromCatalog) return fromCatalog;

      return buildCard(
        {
          guideId,
          guideGuid: pick(purchase, "guideGuid"),
          code: pick(purchase, "code"),
          name: pick(purchase, "name"),
          price: pick(purchase, "montoPagado"),
          currencyCode: pick(purchase, "currencyCode"),
          hasFreeVariant: false,
          hasPaidVariant: true,
        },
        purchase,
        detailsByGuideId[guideId],
        fallbackTextByGuideId[guideId]
      );
    });
  }, [cards, purchases, detailsByGuideId, fallbackTextByGuideId]);

  const saleCards = useMemo(
    () => (onlyPaid ? cards.filter((card) => card.hasPaidVariant) : cards),
    [cards, onlyPaid]
  );

  const isPurchasedFilter = filter === GUIDES2_FILTERS.PURCHASED;

  // Las compradas SI se pueden paginar con numeros: la lista llega completa en
  // una sola llamada, asi que aqui se sabe cuantas hay y cuantas paginas son.
  // El catalogo no —el servidor pagina de 12 en 12 y no devuelve el total—, por
  // eso ese sigue con "Anterior / Siguiente".
  //
  // La pagina se guarda aparte de la del catalogo: moverse entre las compradas
  // no debe arrastrar al catalogo a pedirle otra pagina al servidor.
  const purchasedTotalPages = Math.max(
    1,
    Math.ceil(purchasedCards.length / GUIDES2_PAGE_SIZE)
  );

  // La pagina guardada se recorta a lo que hoy existe en vez de corregirse con
  // un efecto: si estando en la ultima pagina desaparece una compra, se muestra
  // la nueva ultima en el mismo render, sin pasar por una pantalla vacia.
  const purchasedCurrentPage = Math.min(purchasedPage, purchasedTotalPages);

  const pagedPurchasedCards = useMemo(
    () =>
      purchasedCards.slice(
        (purchasedCurrentPage - 1) * GUIDES2_PAGE_SIZE,
        purchasedCurrentPage * GUIDES2_PAGE_SIZE
      ),
    [purchasedCards, purchasedCurrentPage]
  );

  return {
    filter,
    setFilter,
    visibleCards: isPurchasedFilter ? pagedPurchasedCards : saleCards,
    loading,
    error,
    page: isPurchasedFilter ? purchasedCurrentPage : page,
    // Cuantas paginas hay en total. Solo lo sabe la vista de compradas; en el
    // catalogo es null y eso es lo que le dice a la pantalla que ahi no puede
    // numerar las paginas.
    totalPages: isPurchasedFilter ? purchasedTotalPages : null,
    totalItems: isPurchasedFilter ? purchasedCards.length : null,
    pageSize: GUIDES2_PAGE_SIZE,
    hasNextPage: isPurchasedFilter
      ? purchasedCurrentPage < purchasedTotalPages
      : rawItems.length === GUIDES2_PAGE_SIZE,
    // La pagina trajo guias, pero ninguna se vende. No es lo mismo que un
    // catalogo vacio y la pantalla no debe decirlo igual.
    emptyByPaidFilter: !isPurchasedFilter && cards.length > 0 && saleCards.length === 0,
    showPagination: isPurchasedFilter ? purchasedTotalPages > 1 : true,
    goToPage: (nextPage) => {
      const destino = Math.max(1, Number(nextPage) || 1);
      if (isPurchasedFilter) setPurchasedPage(Math.min(destino, purchasedTotalPages));
      else setPage(destino);
    },
    goToPreviousPage: () =>
      isPurchasedFilter
        ? setPurchasedPage((prev) => Math.max(1, prev - 1))
        : setPage((prev) => Math.max(1, prev - 1)),
    goToNextPage: () =>
      isPurchasedFilter
        ? setPurchasedPage((prev) => Math.min(prev + 1, purchasedTotalPages))
        : setPage((prev) => prev + 1),
    // Las tarjetas estan pintadas pero todavia incompletas: falta el detalle,
    // que es de donde salen el nombre traducido y el acceso por variante.
    completingCards: pendingDetails > 0,
    reload: loadCatalog,
    // VIVA-1214: tras pagar una guia se vuelve a preguntar por el acceso de esa
    // guia —compras Y detalle, que son las dos fuentes de `buildCard`— para que
    // su tarjeta pase a "Leer version completa" sin recargar la pagina.
    refreshGuideAccess,
    // Y, si el webhook todavia no ha corrido, quedarse pendiente de que corra.
    awaitGuideActivation,
    activatingGuideId: activation?.guideId ?? null,
    activatedGuideId,
    activationExpired,
  };
};

export { GUIDE_VARIANTS };
